import { useEffect, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../admin/AuthContext';

// Admin-only saved gear combinations for the FOV calculator. Stored in the
// `fov_setups` collection, which firestore.rules restricts to the admin UID
// for reads as well as writes, so these never reach public visitors.
const SavedSetups = ({ currentSetup, onLoad, onClear }) => {
    const { isAdmin } = useAuth();
    const [setups, setSetups] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [name, setName] = useState('');
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isAdmin) return;

        const loadSetups = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'fov_setups'));
                const list = snapshot.docs
                    .map((setupDoc) => ({ id: setupDoc.id, ...setupDoc.data() }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setSetups(list);
            } catch (error) {
                console.error('Error loading FOV setups:', error);
                setStatus('Could not load saved setups.');
            }
        };

        loadSetups();
    }, [isAdmin]);

    if (!isAdmin) return null;

    const handleSelect = (id) => {
        setSelectedId(id);
        setStatus('');
        const setup = setups.find((item) => item.id === id);
        if (setup) onLoad(setup.gear);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;

        setBusy(true);
        try {
            const setup = { name: trimmed, gear: currentSetup };
            const ref = await addDoc(collection(db, 'fov_setups'), { ...setup, created_at: serverTimestamp() });
            setSetups((prev) => [...prev, { id: ref.id, ...setup }].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedId(ref.id);
            setName('');
            setStatus(`Saved "${trimmed}".`);
        } catch (error) {
            console.error('Error saving FOV setup:', error);
            setStatus('Could not save this setup.');
        } finally {
            setBusy(false);
        }
    };

    const handleClear = () => {
        setSelectedId('');
        setStatus('');
        onClear();
    };

    const handleDelete = async () => {
        const setup = setups.find((item) => item.id === selectedId);
        if (!setup || !window.confirm(`Delete "${setup.name}"?`)) return;

        setBusy(true);
        try {
            await deleteDoc(doc(db, 'fov_setups', setup.id));
            setSetups((prev) => prev.filter((item) => item.id !== setup.id));
            setSelectedId('');
            setStatus(`Deleted "${setup.name}".`);
        } catch (error) {
            console.error('Error deleting FOV setup:', error);
            setStatus('Could not delete this setup.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className='fov-card fov-setups-card'>
            <p className='fov-card-label'>My setups</p>

            <div className='fov-setups-row'>
                <div>
                    <label className='fov-field-label' htmlFor='saved-setup-select'>Load a saved setup</label>
                    <div className='fov-setups-controls'>
                        <select
                            id='saved-setup-select'
                            value={selectedId}
                            onChange={(e) => handleSelect(e.target.value)}
                            disabled={setups.length === 0}
                        >
                            <option value=''>{setups.length ? 'Choose a setup' : 'No saved setups yet'}</option>
                            {setups.map((setup) => (
                                <option value={setup.id} key={setup.id}>{setup.name}</option>
                            ))}
                        </select>
                        <button
                            type='button'
                            className='fov-setups-button'
                            onClick={handleClear}
                            disabled={busy}
                        >
                            Clear
                        </button>
                        <button
                            type='button'
                            className='fov-setups-button'
                            onClick={handleDelete}
                            disabled={!selectedId || busy}
                        >
                            Delete
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSave}>
                    <label className='fov-field-label' htmlFor='setup-name-input'>Save current gear as</label>
                    <div className='fov-setups-controls'>
                        <input
                            id='setup-name-input'
                            type='text'
                            placeholder='e.g. RedCat 51 + 2600MC'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <button type='submit' className='fov-setups-button' disabled={!name.trim() || busy}>
                            Save
                        </button>
                    </div>
                </form>
            </div>

            {status ? <p className='fov-setups-status'>{status}</p> : null}
        </div>
    );
};

export default SavedSetups;
